import {
  SyntheticEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { bindPopover, usePopupState } from 'material-ui-popup-state/hooks';

import {
  List,
  ListSearchInput,
  ListSearchInputProps,
} from '@cast/design-system';
import { Organization, OrganizationType } from '@cast/types';

import { useTrial } from '@cast/shared-trial-ui';

import { HeaderPopover } from '../HeaderPopover';
import { CreateOrganizationButton } from './_components/CreateOrganizationButton';
import { EnterpriseHeader } from './_components/EnterpriseHeader';
import { OrganizationList } from './_components/OrganizationList';
import { OrganizationMenuTrigger } from './_components/OrganizationMenuTrigger';

type Actions = {
  onChangeOrganization?: (organization: Organization) => void;
  onCreateNewOrganization?: (enterpriseOrgId?: string) => void;
};

type OrganizationMenuProps = {
  activeOrganizationId?: Organization['id'];
  organizations?: Organization[];
} & Actions;

export const OrganizationMenu = ({
  activeOrganizationId,
  organizations,
  onChangeOrganization,
  onCreateNewOrganization,
}: OrganizationMenuProps) => {
  const { isEligible } = useTrial() || {}; // Needed for storybook to work properly
  const [organizationFilter, setOrganizationFilter] = useState<string>('');
  const [selectedEnterprise, setSelectedEnterprise] = useState<
    Organization | undefined
  >(undefined);

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'profile-menu',
  });

  const activeOrganization = useMemo(() => {
    return organizations?.find((o) => o.id === activeOrganizationId);
  }, [organizations, activeOrganizationId]);

  const activeOrganizationParent = useMemo(() => {
    return organizations?.find((o) => o.id === activeOrganization?.parentId);
  }, [organizations, activeOrganization]);

  // Handle setting selected enterprise when active organization changes
  useEffect(() => {
    if (activeOrganization?.type === OrganizationType.ENTERPRISE) {
      setSelectedEnterprise(activeOrganization);
      return;
    }

    if (activeOrganization?.parentId && activeOrganizationParent) {
      setSelectedEnterprise(activeOrganizationParent);
      return;
    }

    setSelectedEnterprise(undefined);
  }, [
    activeOrganization,
    activeOrganization?.parentId,
    activeOrganizationParent,
  ]);

  // Handle search input change
  const handleOnChange = useCallback<
    Required<ListSearchInputProps>['onChange']
  >((e: SyntheticEvent, value: string) => {
    e.stopPropagation();
    setOrganizationFilter(value);
  }, []);

  // Handle clearing the search input
  const handleOnClear = useCallback((e: ReactMouseEvent) => {
    e.stopPropagation();
    setOrganizationFilter('');
  }, []);

  // Handle organization selection
  const handleChangeOrganization = useCallback(
    (newOrganization: Organization) => {
      if (!activeOrganization) return;

      // Reset filter and call the change callback
      setOrganizationFilter('');
      popupState.close();
      onChangeOrganization?.(newOrganization);
    },
    [activeOrganization, onChangeOrganization, popupState]
  );

  // Handle enterprise navigation
  const handleSelectEnterprise = useCallback(
    (enterprise: Organization | undefined) => {
      setSelectedEnterprise(enterprise);
      setOrganizationFilter('');
    },
    []
  );

  const handleCreateNewOrganization = useCallback(() => {
    popupState.close();
    onCreateNewOrganization?.(selectedEnterprise?.id);
  }, [onCreateNewOrganization, selectedEnterprise?.id, popupState]);

  const handleBackToMain = useCallback(
    () => handleSelectEnterprise(undefined),
    [handleSelectEnterprise]
  );

  const handlePopoverClose = useCallback(() => {
    if (activeOrganization?.parentId) {
      setSelectedEnterprise(activeOrganizationParent);
    }
    setOrganizationFilter('');
    popupState.close();
  }, [activeOrganization?.parentId, activeOrganizationParent, popupState]);

  if (!activeOrganization) {
    return null;
  }

  const isMenuOpen = popupState.isOpen;

  return (
    <>
      <OrganizationMenuTrigger
        activeOrganization={activeOrganization}
        activeOrganizationParent={activeOrganizationParent}
        popupState={popupState}
      />

      <HeaderPopover {...bindPopover(popupState)} onClose={handlePopoverClose}>
        <List
          size="medium"
          disablePadding
          sx={{ minWidth: '270px' }}
          testId="org-menu-list"
        >
          {selectedEnterprise && (
            <EnterpriseHeader
              selectedEnterprise={selectedEnterprise}
              selectedEnterpriseChildrenCount={
                organizations?.filter(
                  (o) => o.parentId === selectedEnterprise.id
                ).length || 0
              }
              childOrganizations={
                organizations?.filter(
                  (o) => o.parentId === selectedEnterprise.id
                ) || []
              }
              activeOrganizationId={activeOrganizationId}
              onBack={handleBackToMain}
            />
          )}

          <ListSearchInput
            placeholder="Search"
            listItemProps={{ component: 'li' as const }}
            clearButtonProps={{ onClick: handleOnClear }}
            value={organizationFilter}
            onChange={handleOnChange}
            autoFocus
            autoComplete="off"
            showClearButton
            testId="orgs-search"
          />

          <OrganizationList
            organizations={organizations}
            organizationFilter={organizationFilter}
            selectedEnterprise={selectedEnterprise}
            activeOrganizationId={activeOrganizationId}
            isMenuOpen={isMenuOpen}
            onSelectEnterprise={handleSelectEnterprise}
            onSelectOrganization={handleChangeOrganization}
          />

          {/* Only show new organization option if user is not in trial mode */}
          {!isEligible && (
            <CreateOrganizationButton
              onCreateNewOrganization={handleCreateNewOrganization}
              selectedEnterpriseId={selectedEnterprise?.id}
            />
          )}
        </List>
      </HeaderPopover>
    </>
  );
};
